/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import { alias } from '@ember/object/computed';
import Component from '@glimmer/component';
import { set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import RouterService from '@ember/routing/router-service';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import CampaignModel, { FormTriggeredCampaignFormTriggerFilter } from 'cio/models/campaign';
import MultiSplitAbstract, { EdgeAbstract } from 'cio/app-models/action-abstracts/multi-split';
import deserializeAndNormalizeFormAttributeFilters from 'cio/utils/campaigns/deserialize-normalize-form-attribute-filters';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';
import { SpecialDeviceAttributes } from 'cio/app-models/conditions/device-attribute';
import FeaturesService from 'ember-feature-flags/services/features';
import { guidFor } from '@ember/object/internals';

interface EditorArgs {
  actionAbstract: MultiSplitAbstract;
  onSelect: () => unknown;
  onDeselect: () => unknown;
  isSaving: boolean;
  hasReadOnlyPermission: boolean;
}

export default class Editor extends Component<EditorArgs> {
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service router!: RouterService;
  @service features!: FeaturesService;

  _segments!: {
    value: string;
    label: string;
    icon: string;
  }[];
  _segmentsForEdges!: Record<string, string>;

  readonly groupName = guidFor(this);

  // Prevent the same analytics event from firing when errors are re-computed
  @tracked trackedErrors: string[] = [];

  @alias('environment.sentAttributes') _sentAttributes!: string[];

  get campaign() {
    return this.args.actionAbstract.action.belongsTo('campaign').value() as CampaignModel | null;
  }

  @computed('campaign.{isFormTriggered,attributeFilters}', 'facebookLeadAds.forms.[]')
  get campaignEventFormsNames() {
    let { campaign } = this;

    if (campaign == null || !campaign.isFormTriggered) {
      return null;
    }

    let filters = deserializeAndNormalizeFormAttributeFilters(campaign.attributeFilters);
    let formIDs = filters.children.map((f: FormTriggeredCampaignFormTriggerFilter) => f.children[0].value);

    return formIDs.map(
      (id: string) =>
        // won't find any forms if lead ads integration is disabled: display form ID as fallback
        this.facebookLeadAds.formsByFormID[id]?.formNameForDisplay || `Facebook Form with ID of ${id}`,
    );
  }

  @computed('sentAttributes.[]')
  get attributes() {
    return [...this._sentAttributes];
  }

  constructor(owner: unknown, args: EditorArgs) {
    super(owner, args);

    let segments = this.store.peekAll('short-segment').map((segment) => ({
      value: segment.id,
      label: segment.name,
      icon: segment.icon,
    }));

    set(this, '_segments', segments);

    let segmentsForEdges: Record<string, string> = {};
    segments.forEach((segment) => {
      segmentsForEdges[segment.value] = segment.label;
    });

    set(this, '_segmentsForEdges', segmentsForEdges);
  }

  @computed('campaign.{event,isEventTriggered,isFormTriggered}')
  get _typeDropdownOptions() {
    let { campaign } = this;

    if (campaign == null) {
      return [];
    }

    if (campaign.isWebhookTriggered) {
      return [
        {
          label: 'Trigger attribute',
          value: 'event_attribute',
        },
      ];
    }

    let options = [
      {
        label: 'Attribute',
        value: 'attribute',
      },
      {
        label: 'Device',
        value: 'device_attribute',
      },
      {
        label: 'Event attribute',
        value: 'event_attribute',
      },
      {
        label: 'Segment',
        value: 'segments_negatable',
      },
    ];

    let { isEventTriggered, isFormTriggered, event } = campaign;

    // only show event_attribute when the campaign is event or form triggered
    if (!isEventTriggered && !isFormTriggered) {
      options = options.filter((option) => option.value !== 'event_attribute');
    }

    let isDeviceConditionEnabled = this.environment.enabledAction('push');
    if (!isDeviceConditionEnabled) {
      options = options.filter((option) => option.value !== 'device_attribute');
    }

    return options.map((option) => {
      if (option.value !== 'event_attribute') {
        return option;
      }

      return {
        ...option,
        isDisabled: (isEventTriggered || isFormTriggered) && isEmpty(event),
      };
    });
  }

  @computed('args.actionAbstract.condition.{type,field}')
  get maxPathCount() {
    let maxSupportedPaths = 20;
    let { condition } = this.args.actionAbstract;
    if (condition.type === 'device_attribute' && condition.field === SpecialDeviceAttributes.id) {
      return 2;
    }
    return maxSupportedPaths;
  }

  @computed('args.actionAbstract.edgeAbstracts.length')
  get onePathRemaining() {
    return this.args.actionAbstract.edgeAbstracts.length === 1;
  }

  @action
  onSortableDrop({
    sourceList,
    sourceIndex,
    targetList,
    targetIndex,
    item,
  }: {
    sourceList: EdgeAbstract[];
    sourceIndex: number;
    targetList: EdgeAbstract[];
    targetIndex: number;
    item: EdgeAbstract;
  }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }
    sourceList.removeAt(sourceIndex);
    targetList.insertAt(targetIndex, item);
  }

  @action
  removeEdge(edge: unknown) {
    this.args.actionAbstract.removeEdge(edge);
  }

  @action
  addEdge() {
    this.args.actionAbstract.addEdge();
  }

  @action
  selectConditionType({ value }: { value: string }) {
    this.args.actionAbstract.selectConditionType(value);
  }

  @action
  onSelectSegmentForEdge(edge: EdgeAbstract, selection: { value: string }) {
    // @ts-ignore: setting nested prop
    set(edge, 'conditionModel.segments', [selection.value]);
  }

  @action
  onSelectAttribute(attr: string) {
    this.args.actionAbstract.setConditionField(attr);
  }

  @action
  trackErrorChange(_: HTMLElement, [errors]: [string[]?]) {
    if (errors?.length && !this.trackedErrors.includes(errors[0])) {
      this.trackedErrors = [...this.trackedErrors, errors[0]];

      this.analytics.track('user', 'error_encountered', {
        currentUrl: this.router.currentURL,
        message: errors[0],
        source: 'Multi-Split Branch',
      });
    }
  }
}
